import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  ArrowLeft, Check, Loader2, Mail, MailCheck, MessageSquare, Trash2, UserCog,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  addContactInquiryNote,
  deleteContactInquiry,
  deleteContactInquiryNote,
  getContactInquiry,
  getPortalEmailDirectory,
  updateContactInquiry,
  updateContactInquiryNote,
} from '@/lib/api';
import {
  activityKindLabel,
  displayValue,
  formatDateTime,
  inquiryStatusChoices,
  inquiryStatusMeta,
  inquirySubjectLabel,
  localeLabel,
  sourcePageLabel,
} from '@/lib/websiteEngagement';
import WebsiteEngagementShell from '@/components/admin/WebsiteEngagementShell';
import PortalEmailComposer from '@/components/admin/PortalEmailComposer';
import NotesThread from '@/components/admin/NotesThread';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { StatusPill, adminInputClass } from '@/components/admin/AdminTableStyles';
import { cn } from '@/lib/utils';

const UNASSIGNED = 'unassigned'; // Radix Select cannot use "" as a value

function safeList(value) {
  return Array.isArray(value) ? value : [];
}

function errorText(error, fallback) {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail?.message) return detail.message;
  return fallback;
}

/** A read-only field: never blank, never hidden. */
function Field({ label, children }) {
  return (
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">
        {label}
      </p>
      <p className="mt-0.5 break-words text-sm text-[#062B1A]/85">{children}</p>
    </div>
  );
}

/**
 * The delete dialog. Typing the reference is the confirmation, exactly as it
 * is for a registration — a click-through cannot destroy somebody's message.
 */
function DeleteInquiryDialog({ open, inquiry, busy, onCancel, onConfirm }) {
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const expected = String(inquiry?.reference || '');
  const matches = typed.trim().toUpperCase() === expected.toUpperCase();

  useEffect(() => {
    if (open) {
      setTyped('');
      setReason('');
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={(next) => !busy && !next && onCancel()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Permanently delete this inquiry?</DialogTitle>
          <DialogDescription>
            <span className="block text-[#062B1A]">{expected}</span>
            <span className="mt-1 block">
              The visitor&apos;s message, every internal note and the record of any reply
              already sent are removed. This cannot be undone.
            </span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="confirm-reference">Type {expected} to confirm</Label>
            <Input
              id="confirm-reference"
              value={typed}
              disabled={busy}
              onChange={(event) => setTyped(event.target.value)}
              className={adminInputClass}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="delete-reason">Reason (optional)</Label>
            <Input
              id="delete-reason"
              value={reason}
              disabled={busy}
              placeholder="Spam, duplicate, privacy request…"
              onChange={(event) => setReason(event.target.value)}
              className={adminInputClass}
            />
          </div>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
            Keep it
          </Button>
          <Button
            type="button"
            disabled={busy || !matches}
            onClick={() => onConfirm({ confirm_reference: typed.trim(), reason: reason.trim() })}
            className="bg-red-700 text-white hover:bg-red-800"
          >
            {busy ? <Loader2 className="mr-2 size-4 animate-spin" /> : <Trash2 className="mr-2 size-4" />}
            Delete permanently
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminContactInquiryDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();

  const [inquiry, setInquiry] = useState(null);
  const [permissions, setPermissions] = useState({});
  const [permissionHints, setPermissionHints] = useState({});
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletingNoteId, setDeletingNoteId] = useState('');
  const [staff, setStaff] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await getContactInquiry(id);
      setInquiry(data);
      setPermissions(data?.permissions || {});
      setPermissionHints(data?.permission_hints || {});
      setNotFound(false);
      return data;
    } catch (error) {
      console.error(error);
      if (error?.response?.status === 404) setNotFound(true);
      else toast.error(errorText(error, 'Could not load this inquiry'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (authLoading) return;
    load();
  }, [authLoading, load]);

  // The owner list is the same staff directory the email composer searches, so
  // "who can own this" and "who can be emailed about it" stay one answer.
  useEffect(() => {
    let cancelled = false;
    getPortalEmailDirectory('')
      .then(({ data }) => {
        if (cancelled) return;
        setStaff(safeList(data?.items).filter((item) => item.kind === 'staff'));
      })
      .catch((error) => { if (!cancelled) console.error(error); });
    return () => { cancelled = true; };
  }, []);

  // Opening an inquiry is reading it. Done once, and only when it is actually
  // unread, so re-rendering cannot spam the API.
  useEffect(() => {
    if (!inquiry?.id || !inquiry.unread) return;
    updateContactInquiry(inquiry.id, { unread: false })
      .then(({ data }) => setInquiry((current) => (current ? { ...current, ...data } : current)))
      .catch((error) => console.error(error));
  }, [inquiry?.id, inquiry?.unread]);

  const patch = async (payload, successMessage) => {
    if (!inquiry?.id) return;
    try {
      setBusy(true);
      const { data } = await updateContactInquiry(inquiry.id, payload);
      setInquiry((current) => ({ ...current, ...data }));
      if (successMessage) toast.success(successMessage);
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'Could not update this inquiry'));
    } finally {
      setBusy(false);
    }
  };

  const addNote = async (body, parentId) => {
    try {
      await addContactInquiryNote(inquiry.id, { body, parent_id: parentId || undefined });
      await load();
      toast.success('Note added');
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'Could not add the note'));
    }
  };

  const editNote = async (note, body) => {
    try {
      await updateContactInquiryNote(inquiry.id, note.id, { body });
      await load();
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'Could not save the note'));
    }
  };

  const removeNote = async (note) => {
    try {
      setDeletingNoteId(note.id);
      await deleteContactInquiryNote(inquiry.id, note.id);
      await load();
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'Could not delete the note'));
    } finally {
      setDeletingNoteId('');
    }
  };

  const runDelete = async (payload) => {
    try {
      setBusy(true);
      await deleteContactInquiry(inquiry.id, payload);
      toast.success(`${inquiry.reference} permanently deleted`);
      setDeleteOpen(false);
      navigate('/admin/website-engagement/contact-inquiries');
    } catch (error) {
      console.error(error);
      toast.error(errorText(error, 'Could not delete this inquiry'));
    } finally {
      setBusy(false);
    }
  };

  const statusMeta = inquiryStatusMeta(inquiry?.status);
  const canDelete = permissions['inquiries.delete'] === true;
  const deleteHint = permissionHints['inquiries.delete']
    || 'Only a full administrator can permanently delete an inquiry.';

  // The composer resolves the address and merge values server-side; this only
  // says which record it is about and what the reply should start out saying.
  const recipients = useMemo(
    () => (inquiry ? [{ id: inquiry.id, label: inquiry.full_name, email: inquiry.email }] : []),
    [inquiry],
  );
  const replySubject = inquiry
    ? `Re: ${inquiry.subject_label || inquirySubjectLabel(inquiry.subject, inquiry.subject_original)}`
    : '';

  if (loading) {
    return (
      <WebsiteEngagementShell title="Contact inquiry">
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="flex items-center gap-2 p-8 text-sm text-[#062B1A]/50">
            <Loader2 className="size-4 animate-spin" /> Loading this inquiry…
          </CardContent>
        </Card>
      </WebsiteEngagementShell>
    );
  }

  if (notFound || !inquiry) {
    return (
      <WebsiteEngagementShell title="Contact inquiry">
        <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
          <CardContent className="space-y-3 p-8 text-center">
            <p className="text-sm text-[#062B1A]/60">
              This inquiry no longer exists. It may have been deleted.
            </p>
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/website-engagement/contact-inquiries')}
              className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
            >
              <ArrowLeft className="mr-2 size-4" /> Back to Contact Form Inquiries
            </Button>
          </CardContent>
        </Card>
      </WebsiteEngagementShell>
    );
  }

  return (
    <WebsiteEngagementShell
      title={displayValue(inquiry.full_name)}
      description={`${inquiry.reference} · received ${formatDateTime(inquiry.created_at)}`}
      actions={(
        <>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => navigate('/admin/website-engagement/contact-inquiries')}
            className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
          >
            <ArrowLeft className="mr-2 size-4" /> Back
          </Button>
          <PortalEmailComposer
            audience="contact_inquiries"
            recipients={recipients}
            buttonLabel="Reply by email"
            buttonVariant="default"
            buttonClassName="bg-[#062B1A] text-white hover:bg-[#041B10]"
            defaults={{ subject: replySubject }}
            onSent={load}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canDelete || busy}
            title={canDelete ? undefined : deleteHint}
            onClick={() => setDeleteOpen(true)}
            className="border-red-200 text-red-700 hover:bg-red-50"
          >
            <Trash2 className="mr-2 size-4" /> Delete
          </Button>
        </>
      )}
    >
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="space-y-4">
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="space-y-4 p-5">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h2
                  className="text-base font-semibold text-[#062B1A]"
                  style={{ fontFamily: 'var(--font-display)' }}
                >
                  {inquiry.subject_label
                    || inquirySubjectLabel(inquiry.subject, inquiry.subject_original)}
                </h2>
                <StatusPill tone={statusMeta.pillTone} data-testid="inquiry-status-pill">
                  {statusMeta.label}
                </StatusPill>
              </div>
              {/* Visitor text is rendered as text, never as HTML. */}
              <p
                data-testid="inquiry-message"
                className="whitespace-pre-wrap break-words rounded-lg border border-[rgba(6,43,26,0.08)] bg-[#F7F8FA] p-4 text-sm leading-relaxed text-[#062B1A]/85"
              >
                {inquiry.message}
              </p>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <Field label="Email">
                  <a
                    href={`mailto:${inquiry.email}`}
                    className="underline decoration-[#D4AF37] underline-offset-2"
                  >
                    {displayValue(inquiry.email)}
                  </a>
                </Field>
                <Field label="Phone">{displayValue(inquiry.phone)}</Field>
                <Field label="Language">{localeLabel(inquiry.locale)}</Field>
                <Field label="Sent from">{sourcePageLabel(inquiry.source_page)}</Field>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="space-y-4 p-5">
              <h3
                className="flex items-center gap-2 text-base font-semibold text-[#062B1A]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                <MailCheck className="size-4 text-[#D4AF37]" />
                Replies sent
              </h3>
              {safeList(inquiry.replies).length === 0 ? (
                <p className="text-sm text-[#062B1A]/45" data-testid="no-replies">
                  No reply has been sent yet. Use “Reply by email” above — the inquiry moves to
                  Replied only once the message actually goes out.
                </p>
              ) : (
                <ul className="space-y-3" data-testid="reply-list">
                  {safeList(inquiry.replies).map((reply) => (
                    <li
                      key={reply.id}
                      data-testid="sent-reply"
                      className="rounded-lg border border-[rgba(6,43,26,0.10)] bg-white p-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-[#062B1A]">{reply.subject}</p>
                        <span className="text-[11px] text-[#062B1A]/50">
                          {formatDateTime(reply.sent_at)}
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-[#062B1A]/55">
                        To {reply.to} · sent by {reply.staff_user_name || 'Staff member'}
                        {reply.template_name ? ` · template “${reply.template_name}”` : ' · written from scratch'}
                        {' · '}
                        {reply.delivery_status === 'sent' ? 'delivered to Gmail' : reply.delivery_status}
                      </p>
                      <p className="mt-2 whitespace-pre-wrap break-words text-sm text-[#062B1A]/80">
                        {reply.body}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="space-y-4 p-5">
              <h3
                className="flex items-center gap-2 text-base font-semibold text-[#062B1A]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                <MessageSquare className="size-4 text-[#D4AF37]" />
                Internal notes
              </h3>
              {/* The same threaded notes as applications, students, agencies and
                  signups — @-mentions notify, replies thread, edits are marked. */}
              <NotesThread
                isSuperAdmin={user?.role === 'admin' && user?.is_super_admin === true}
                notes={safeList(inquiry.notes)}
                currentUserId={user?.id || ''}
                onAdd={addNote}
                onEdit={editNote}
                onDelete={removeNote}
                deletingId={deletingNoteId}
                emptyText="No notes yet. Add the first."
              />
            </CardContent>
          </Card>
        </div>

        <div className="space-y-4">
          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="space-y-4 p-5">
              <h3
                className="flex items-center gap-2 text-base font-semibold text-[#062B1A]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                <UserCog className="size-4 text-[#D4AF37]" />
                Handling
              </h3>

              <div className="space-y-1.5">
                <Label htmlFor="inquiry-status">Status</Label>
                <Select
                  value={inquiry.status || 'new'}
                  disabled={busy}
                  onValueChange={(value) => patch({ status: value }, 'Status updated')}
                >
                  <SelectTrigger id="inquiry-status" aria-label="Inquiry status" className={cn('h-9', adminInputClass)}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {inquiryStatusChoices(inquiry.status).map((status) => (
                      <SelectItem key={status} value={status}>
                        {inquiryStatusMeta(status).label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {inquiry.status === 'replied' && (
                  <p className="text-[11px] text-[#062B1A]/45">
                    Set by a reply that was actually sent — it cannot be chosen by hand.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="inquiry-owner">Owner</Label>
                <Select
                  value={inquiry.owner_user_id || UNASSIGNED}
                  disabled={busy}
                  onValueChange={(value) => patch(
                    { owner_user_id: value === UNASSIGNED ? '' : value },
                    value === UNASSIGNED ? 'Owner cleared' : 'Owner assigned',
                  )}
                >
                  <SelectTrigger id="inquiry-owner" aria-label="Inquiry owner" className={cn('h-9', adminInputClass)}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={UNASSIGNED}>Nobody assigned</SelectItem>
                    {staff.map((person) => (
                      <SelectItem key={person.id} value={person.id}>{person.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={busy}
                  onClick={() => patch({ unread: !inquiry.unread },
                    inquiry.unread ? 'Marked read' : 'Marked unread')}
                  className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
                >
                  <Check className="mr-1.5 size-3.5" />
                  {inquiry.unread ? 'Mark read' : 'Mark unread'}
                </Button>
                {inquiry.status === 'closed' ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => patch({ status: 'open' }, 'Inquiry reopened')}
                    className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
                  >
                    Reopen
                  </Button>
                ) : (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={busy}
                    onClick={() => patch({ status: 'closed' }, 'Inquiry closed')}
                    className="border-[rgba(6,43,26,0.16)] text-[#062B1A]"
                  >
                    Close
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="border border-[rgba(6,43,26,0.08)] shadow-sm">
            <CardContent className="space-y-3 p-5">
              <h3
                className="flex items-center gap-2 text-base font-semibold text-[#062B1A]"
                style={{ fontFamily: 'var(--font-display)' }}
              >
                <Mail className="size-4 text-[#D4AF37]" />
                Activity
              </h3>
              {safeList(inquiry.activity).length === 0 ? (
                <p className="text-sm text-[#062B1A]/45">Nothing has happened yet.</p>
              ) : (
                <ol className="space-y-3" data-testid="activity-timeline">
                  {safeList(inquiry.activity).map((entry) => (
                    <li key={entry.id} data-testid="activity-entry" className="border-l-2 border-[#D4AF37]/40 pl-3">
                      <p className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#062B1A]/45">
                        {activityKindLabel(entry.kind)}
                      </p>
                      <p className="text-sm text-[#062B1A]/80">{entry.summary}</p>
                      <p className="text-[11px] text-[#062B1A]/45">
                        {entry.actor_name} · {formatDateTime(entry.created_at)}
                      </p>
                    </li>
                  ))}
                </ol>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      <DeleteInquiryDialog
        open={deleteOpen}
        inquiry={inquiry}
        busy={busy}
        onCancel={() => setDeleteOpen(false)}
        onConfirm={runDelete}
      />
    </WebsiteEngagementShell>
  );
}
